// src/components/black-friday/comparison-charts.tsx
import React from "react";
import { BFDailyCampaignData } from "@/types/black-friday";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface Props {
  data25: BFDailyCampaignData[];
  data24: BFDailyCampaignData[];
  data23: BFDailyCampaignData[];
  comparisonPeriod: 'year2024_total' | 'year2024_same' | 'year2023_total' | 'year2023_same';
  maxIndex25: number;
  onChartClick?: (title: string, data: ChartDataPoint[], format: "number" | "currency" | "percentage") => void;
}

interface ChartDataPoint {
  index: number;
  [key: string]: number;
}

function prepareChartData(
  data25: BFDailyCampaignData[],
  data24: BFDailyCampaignData[],
  data23: BFDailyCampaignData[],
  metric: keyof BFDailyCampaignData,
  maxIndex?: number
): ChartDataPoint[] {
  // Create a map of index to values
  const dataMap = new Map<number, { year2025: number; year2024: number; year2023: number }>();

  data25.forEach((row) => {
    const existing = dataMap.get(row.index) || { year2025: 0, year2024: 0, year2023: 0 };
    existing.year2025 += Number(row[metric]) || 0;
    dataMap.set(row.index, existing);
  });

  data24.forEach((row) => {
    const existing = dataMap.get(row.index) || { year2025: 0, year2024: 0, year2023: 0 };
    existing.year2024 += Number(row[metric]) || 0;
    dataMap.set(row.index, existing);
  });

  data23.forEach((row) => {
    const existing = dataMap.get(row.index) || { year2025: 0, year2024: 0, year2023: 0 };
    existing.year2023 += Number(row[metric]) || 0;
    dataMap.set(row.index, existing);
  });

  // Convert to array and sort by index
  let result = Array.from(dataMap.entries())
    .map(([index, values]) => ({
      index,
      year2025: values.year2025,
      year2024: values.year2024,
      year2023: values.year2023,
    }))
    .sort((a, b) => a.index - b.index);

  // Filter to maxIndex if provided
  if (maxIndex !== undefined) {
    result = result.filter(item => item.index >= -70 && item.index <= maxIndex);
  }

  return result;
}

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const daysText = label > 0 ? `BF +${label}` : label === 0 ? "Black Friday" : `BF ${label}`;

    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold mb-2">{daysText}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

function ComparisonChart({
  title,
  data,
  dataKey,
  format = "number",
  onClick,
}: {
  title: string;
  data: ChartDataPoint[];
  dataKey: string;
  format?: "number" | "currency" | "percentage";
  onClick?: () => void;
}) {
  const formatYAxis = (value: number) => {
    if (format === "currency") {
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
      return `$${value.toFixed(0)}`;
    }
    if (format === "percentage") {
      return `${value.toFixed(1)}%`;
    }
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  return (
    <Card className={onClick ? "cursor-pointer hover:shadow-lg transition-shadow" : ""} onClick={onClick}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span>{title}</span>
          {onClick && <span className="text-xs text-muted-foreground font-normal">Click to expand</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data} margin={{ top: 5, right: 5, bottom: 20, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="index"
              label={{ value: "Days from Black Friday", position: "insideBottom", offset: -10 }}
              className="text-xs"
            />
            <YAxis tickFormatter={formatYAxis} className="text-xs" />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: "10px" }} />
            <ReferenceLine
              x={0}
              stroke="hsl(var(--destructive))"
              strokeDasharray="3 3"
              label={{ value: "BF", position: "top" }}
            />
            <Line
              type="monotone"
              dataKey="year2025"
              stroke="#3b82f6"
              strokeWidth={2}
              name="2025"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="year2024"
              stroke="#f97316"
              strokeWidth={2}
              name="2024"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="year2023"
              stroke="#10b981"
              strokeWidth={2}
              name="2023"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function ComparisonCharts({ data25, data24, data23, comparisonPeriod, maxIndex25, onChartClick }: Props) {
  // Determine if we should limit x-axis to "same period" range
  const isSamePeriod = comparisonPeriod === 'year2024_same' || comparisonPeriod === 'year2023_same';
  const xAxisMax = isSamePeriod ? maxIndex25 : undefined;

  const charts = [
    {
      title: "Impressions",
      data: prepareChartData(data25, data24, data23, "impr", xAxisMax),
      dataKey: "impr",
      format: "number" as const,
    },
    {
      title: "Cost",
      data: prepareChartData(data25, data24, data23, "cost", xAxisMax),
      dataKey: "cost",
      format: "currency" as const,
    },
    {
      title: "Conversions",
      data: prepareChartData(data25, data24, data23, "conv", xAxisMax),
      dataKey: "conv",
      format: "number" as const,
    },
    {
      title: "Conversion Value",
      data: prepareChartData(data25, data24, data23, "convValue", xAxisMax),
      dataKey: "convValue",
      format: "currency" as const,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {charts.map((chart) => (
        <ComparisonChart
          key={chart.title}
          {...chart}
          onClick={onChartClick ? () => onChartClick(chart.title, chart.data, chart.format) : undefined}
        />
      ))}
    </div>
  );
}
